import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { DateTime } from 'luxon';
import isEqual from 'react-fast-compare';
import { useSelector } from 'react-redux';

import { noop } from 'ui-components/default-props/noop';

/** @typedef {import('../../../../financial-services-ui/types/fsui').AuctanePaySession} AuctanePaySession */

/**
 * @typedef {object} WalletToRegister
 * @property {string} walletId
 * @property {string} brandName
 * @property {string} currencyCode
 * @property {boolean} [isSetupComplete]
 * @property {string[]} [newLabelProviderIds]
 * @property {string[]} [newInsuranceProviderIds]
 * @property {Array<{updateId: string}>} [newResourceUpdates]
 */

/**
 * @typedef {object} RegistrationRequirement
 * @property {string[]} [satisfyingFormTypes]
 */

/**
 * @typedef {Record<string, RegistrationRequirement>} RegistrationRequirements
 */

/**
 * @typedef {Record<string, Array<{termType: string, version: string}>>} NonDefaultTerms
 */

import { useWalletRegistrationFormContext } from './wallet-registration-form-context';
import { US, AU, CA, NZ, MX } from '../../../../constants/country-codes';
import { emptyArray } from 'ui-components/default-props/empty';
import { useEnableInsurancePromoExtended } from '../../../../feature-flags/hooks/use-enable-insurance-promo-extended';
import { useEnableUpsLabelTermsV2 } from '../../../../feature-flags/hooks/use-enable-ups-label-terms-v2';
import Global from '../../../../store/global-selectors';
import { useWallet } from '../../../hooks/use-wallet';
import {
  ADD_FUNDS_TAB,
  FORM_REQUIREMENTS,
  PAYMENT_AND_LOCATION_FORMS,
  PAYMENT_TAB,
  REGISTRATION_REQUIREMENTS,
  SUPPORTED_PAYMENT_METHOD_TYPES,
  TERMS_TAB,
} from '../../../utilities/constants';

// TODO: SPD-23753 - Replace with actual cutoff date when determined
// Accounts created before this date will see the old separate terms tab
// Accounts created on or after this date will see integrated terms in add funds tab
const TERMS_TAB_CUTOFF_DATE = DateTime.fromISO('2026-03-04T00:00:00Z');

const WalletRegistrationModalContext = React.createContext({
  walletToRegister: undefined,
  requiredForms: [],
  isPickupSameAsBilling: true,
  setPaymentType: noop,
  setIsPickupSameAsBilling: noop,
});
export const useWalletRegistrationModalContext = () => useContext(WalletRegistrationModalContext);

const buildRequiredForms = (registrationRequirements, walletForm) => {
  const results = Object.keys(registrationRequirements ?? {}).reduce((aggRequiredForms, requirementId) => {
    if (requirementId === REGISTRATION_REQUIREMENTS.BILLING_ADDRESS) {
      aggRequiredForms.add(FORM_REQUIREMENTS.BILLING_ADDRESS);
    }

    if (requirementId === REGISTRATION_REQUIREMENTS.PICKUP_ADDRESS) {
      aggRequiredForms.add(FORM_REQUIREMENTS.PICKUP_ADDRESS);
    }

    if (requirementId === REGISTRATION_REQUIREMENTS.TAX_ID_INFORMATION) {
      aggRequiredForms.add(FORM_REQUIREMENTS.TAX_ID_INFORMATION);
    }

    if (requirementId === REGISTRATION_REQUIREMENTS.PAYMENT_METHOD) {
      const { satisfyingFormTypes } = registrationRequirements[requirementId];
      if (satisfyingFormTypes?.length > 1) {
        aggRequiredForms.add(FORM_REQUIREMENTS.PAYMENT_METHOD_TYPE);
      }

      const paymentMethodType = walletForm?.[FORM_REQUIREMENTS.PAYMENT_METHOD]?.[FORM_REQUIREMENTS.PAYMENT_METHOD_TYPE];

      switch (paymentMethodType) {
        case SUPPORTED_PAYMENT_METHOD_TYPES.ADYEN_DROP_IN_COMPONENT:
          aggRequiredForms.add(FORM_REQUIREMENTS.ADYEN_SESSION_INFO);
          break;
        case SUPPORTED_PAYMENT_METHOD_TYPES.CREDIT_CARD:
          aggRequiredForms.add(FORM_REQUIREMENTS.CREDIT_CARD);
          break;
        case SUPPORTED_PAYMENT_METHOD_TYPES.STAMPS_CREDENTIALS:
          aggRequiredForms.add(FORM_REQUIREMENTS.STAMPS_CREDENTIALS);
          break;
        default:
          break;
      }
    }

    if (requirementId === REGISTRATION_REQUIREMENTS.IO_BLACK_BOX) {
      aggRequiredForms.add(FORM_REQUIREMENTS.IO_BLACK_BOX);
    }

    return aggRequiredForms;
  }, new Set());

  return Array.from(results);
};

/**
 * @param {object} props
 * @param {React.ReactNode} props.children
 * @param {RegistrationRequirements} props.registrationRequirements
 * @param {Array<{termType: string, version: string}>} props.terms
 * @param {NonDefaultTerms} props.nonDefaultTerms
 * @param {() => void} [props.onSuccess]
 * @param {WalletToRegister} props.walletToRegister
 * @param {() => void} [props.closeWalletModal]
 * @param {() => void} [props.labelPurchaseCallback]
 * @param {() => void} [props.openConfirmationDialog]
 * @param {string[]} [props.selectedFulfillmentPlanIds]
 * @param {string[]} [props.sellerLabelProviderIds]
 * @param {boolean} [props.isUpdate]
 * @param {AuctanePaySession} [props.preloadedSession] - Preloaded session from redirect flow
 * @returns {React.ReactElement}
 */
export const WalletRegistrationModalContextProvider = ({
  children,
  registrationRequirements,
  terms,
  nonDefaultTerms,
  onSuccess = noop,
  walletToRegister,
  closeWalletModal = noop,
  labelPurchaseCallback = noop,
  openConfirmationDialog = noop,
  selectedFulfillmentPlanIds = emptyArray,
  sellerLabelProviderIds = emptyArray,
  isUpdate = false,
  // it is worth commenting here that a preloaded session might come from a redirect flow, where the session was used to complete hookup and we want to keep using its details
  preloadedSession,
}) => {
  const { walletForm } = useWalletRegistrationFormContext();
  const [paymentType, setPaymentType] = useState(null);
  const [requiredForms, setRequiredForms] = useState(() => buildRequiredForms(registrationRequirements, walletForm));
  const [isPickupSameAsBilling, setIsPickupSameAsBilling] = useState(true);
  const requireExactWallet = true;
  const { isSetupComplete } = useWallet(walletToRegister.walletId, requireExactWallet);
  const sellerHomeCountry = useSelector(Global.selectSellerHomeCountry) || US;
  const shouldAutoInsureByCountry = {
    US,
    AU,
    CA,
    NZ,
    MX,
  };
  // SPD-25921: When the extended promo flag is on, hide the insurance toggle
  // and default to false so we don't silently auto-insure packages.
  const isInsurancePromoExtendedEnabled = useEnableInsurancePromoExtended();
  const shouldShowInsuranceToggle = !isInsurancePromoExtendedEnabled;
  const [shouldAutoInsure, setShouldAutoInsure] = useState(
    shouldShowInsuranceToggle && !isUpdate && !!shouldAutoInsureByCountry[sellerHomeCountry],
  );
  const shouldShowAdyenDropInComponent = !!requiredForms.find((form) => form === FORM_REQUIREMENTS.ADYEN_SESSION_INFO);
  const isTermsAvailable = !!terms.length;

  // Check if account was created before the cutoff date for showing old terms tab
  const isUpsLabelTermsV2Enabled = useEnableUpsLabelTermsV2();
  const sellerCreateDateUTC = useSelector(Global.selectSellerCreateDateUTC);
  const accountCreatedDate = sellerCreateDateUTC ? DateTime.fromISO(sellerCreateDateUTC) : null;
  const shouldShowOldTermsTab = !isUpsLabelTermsV2Enabled || (accountCreatedDate && accountCreatedDate < TERMS_TAB_CUTOFF_DATE);

  useEffect(() => {
    setRequiredForms((prevState) => {
      const newState = buildRequiredForms(registrationRequirements, walletForm);
      return isEqual(prevState, newState) ? prevState : newState;
    });
  }, [registrationRequirements, walletForm]);

  const isStepRequired = useCallback(
    (stepId) => {
      if (stepId === PAYMENT_TAB) {
        return requiredForms.some((form) => PAYMENT_AND_LOCATION_FORMS.includes(form));
      }

      if (stepId === TERMS_TAB) {
        // Only show old terms tab if account was created before cutoff date AND terms exist
        return !!terms.length && shouldShowOldTermsTab;
      }
      if (stepId === ADD_FUNDS_TAB) {
        return !!selectedFulfillmentPlanIds?.length || !isSetupComplete;
      }

      return false;
    },
    [requiredForms, terms.length, selectedFulfillmentPlanIds, isSetupComplete, shouldShowOldTermsTab],
  );

  const value = useMemo(
    () => ({
      registrationRequirements,
      isStepRequired,
      walletToRegister,
      onSuccess,
      terms,
      nonDefaultTerms,
      requiredForms,
      isPickupSameAsBilling,
      setIsPickupSameAsBilling,
      closeWalletModal,
      labelPurchaseCallback,
      openConfirmationDialog,
      selectedFulfillmentPlanIds,
      sellerLabelProviderIds,
      shouldAutoInsure,
      setShouldAutoInsure,
      shouldShowInsuranceToggle,
      isTermsAvailable,
      shouldShowAdyenDropInComponent,
      paymentType,
      setPaymentType,
      preloadedSession,
      shouldShowOldTermsTab,
    }),
    [
      registrationRequirements,
      isStepRequired,
      walletToRegister,
      onSuccess,
      terms,
      nonDefaultTerms,
      requiredForms,
      isPickupSameAsBilling,
      setIsPickupSameAsBilling,
      closeWalletModal,
      labelPurchaseCallback,
      openConfirmationDialog,
      selectedFulfillmentPlanIds,
      sellerLabelProviderIds,
      shouldAutoInsure,
      setShouldAutoInsure,
      shouldShowInsuranceToggle,
      isTermsAvailable,
      shouldShowAdyenDropInComponent,
      paymentType,
      setPaymentType,
      preloadedSession,
      shouldShowOldTermsTab,
    ],
  );

  return <WalletRegistrationModalContext.Provider value={value}>{children}</WalletRegistrationModalContext.Provider>;
};
